import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, Sparkles, Save, Check, ChevronRight, ChevronDown,
  MessageSquare, Users, Download, Link2, Loader2, RefreshCw,
  Building2, TrendingUp, AlertTriangle, Compass, Grid3X3,
  Target, Rocket, Layers, MinusCircle, Cpu, Settings, CheckSquare,
  Eye, Edit3, Send, X, Plus, Clock, RotateCcw, Search
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { mockStrategies, mockStrategyComments, strategySections } from '../mock/strategyDevData';
import { useOrg } from '../context/OrgContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Set to true to use mock data, false to use API
const USE_MOCK_DATA = false;

// Icon mapping for sections
const sectionIcons = {
  Building2, TrendingUp, AlertTriangle, Compass, Grid3X3,
  Target, Rocket, Layers, MinusCircle, Cpu, Settings, CheckSquare
};

// Section name overrides for function entities (departments)
const functionSectionOverrides = {
  company_brief: { name: 'Department Brief', description: 'Department overview, scope, and current state' },
  performance_review: { name: 'Department Performance', description: 'Current performance recap and achievements' },
  challenges_mandate: { name: 'Department Challenges', description: 'Key challenges and departmental mandate' },
  vision_mission: { name: 'Department Vision & Mission', description: 'Department vision and mission statements' },
  growth_strategy: { name: 'Department Growth Plan', description: 'Growth and development plans' },
  operational_excellence: { name: 'Department Excellence', description: 'Process improvements and efficiency targets' },
  asks_approvals: { name: 'Department Asks', description: 'Requests for management approval' },
};

// Field label overrides for function entities
const functionFieldOverrides = {
  company_name: 'Department Name',
  industry: 'Function Area',
  size: 'Team Size',
  founded_year: 'Established Year',
  headquarters: 'Location',
  key_products_services: 'Key Services & Capabilities',
};

// Section name overrides for subsidiary entities
const subsidiarySectionOverrides = {
  company_brief: { name: 'Company Brief', description: 'Company overview, industry, and current state' },
};

const statusColors = {
  not_started: 'bg-gray-500/20 text-gray-400',
  in_progress: 'bg-blue-500/20 text-blue-400',
  ai_generated: 'bg-purple-500/20 text-purple-400',
  reviewed: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-green-500/20 text-green-400'
};

const statusLabels = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  ai_generated: 'AI Generated',
  reviewed: 'Reviewed',
  approved: 'Approved'
};

const StrategyEditor = () => {
  const { strategyId } = useParams();
  const navigate = useNavigate();
  const { organization } = useOrg();
  
  const [strategy, setStrategy] = useState(null);
  const [sectionsConfig, setSectionsConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [versionModalData, setVersionModalData] = useState(null);

  // Fetch strategy data
  const fetchStrategy = useCallback(async () => {
    try {
      setLoading(true);
      
      if (USE_MOCK_DATA) {
        // Use mock data
        const foundStrategy = mockStrategies.find(s => s.id === strategyId);
        if (!foundStrategy) {
          toast.error('Strategy not found');
          navigate('/strategy-development');
          return;
        }
        setStrategy(foundStrategy);
        setSectionsConfig(strategySections);
        setComments(mockStrategyComments.filter(c => c.strategy_id === strategyId));
        
        // Set first section as active if none selected
        if (!activeSection && strategySections.length > 0) {
          setActiveSection(strategySections[0].id);
        }
      } else {
        const [strategyRes, configRes, commentsRes] = await Promise.all([
          axios.get(`${API_URL}/api/strategy-dev/projects/${strategyId}`),
          axios.get(`${API_URL}/api/strategy-dev/sections-config`),
          axios.get(`${API_URL}/api/strategy-dev/projects/${strategyId}/comments`)
        ]);
        
        setStrategy(strategyRes.data);
        setSectionsConfig(configRes.data.sections);
        setComments(commentsRes.data);
        
        // Set first section as active if none selected
        if (!activeSection && configRes.data.sections.length > 0) {
          setActiveSection(configRes.data.sections[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching strategy:', error);
      toast.error('Failed to load strategy');
      navigate('/strategy-development');
    } finally {
      setLoading(false);
    }
  }, [strategyId, navigate, activeSection]);

  useEffect(() => {
    fetchStrategy();
  }, [fetchStrategy]);

  // Get current section data
  const getCurrentSection = () => {
    if (!strategy || !activeSection) return null;
    return strategy.sections?.find(s => s.section_id === activeSection);
  };

  // Get section config with entity-specific overrides
  const getSectionConfig = (sectionId) => {
    const config = sectionsConfig.find(s => s.id === sectionId);
    if (!config) return null;

    const entityType = strategy?.entity_type;
    let overrides = null;
    if (entityType === 'function') {
      overrides = functionSectionOverrides[sectionId];
    } else if (entityType === 'subsidiary') {
      overrides = subsidiarySectionOverrides[sectionId];
    }

    if (overrides) {
      return { ...config, ...overrides };
    }
    return config;
  };

  // Calculate progress
  const calculateProgress = () => {
    if (!strategy?.sections) return 0;
    const completed = strategy.sections.filter(
      s => s.status === 'reviewed' || s.status === 'approved'
    ).length;
    return Math.round((completed / strategy.sections.length) * 100);
  };

  // Save section content
  const handleSaveSection = async (sectionId, data, status = null) => {
    try {
      setSaving(true);
      
      if (USE_MOCK_DATA) {
        // Update local state for mock
        setStrategy(prev => ({
          ...prev,
          sections: prev.sections.map(s => 
            s.section_id === sectionId 
              ? { ...s, data, status: status || 'in_progress' }
              : s
          )
        }));
        toast.success('Section saved');
      } else {
        await axios.put(`${API_URL}/api/strategy-dev/projects/${strategyId}/sections`, {
          section_id: sectionId,
          data,
          status: status || 'in_progress'
        });
        
        setStrategy(prev => ({
          ...prev,
          sections: prev.sections.map(s => 
            s.section_id === sectionId 
              ? { ...s, data, status: status || 'in_progress' }
              : s
          )
        }));
        
        toast.success('Section saved');
      }
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error('Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  // Generate AI content
  const handleGenerateAI = async (sectionId) => {
    try {
      setGenerating(true);
      
      if (USE_MOCK_DATA) {
        // Simulate AI generation with mock data
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const mockSuggestions = getMockAISuggestion(sectionId);
        
        setStrategy(prev => ({
          ...prev,
          sections: prev.sections.map(s => 
            s.section_id === sectionId 
              ? { 
                  ...s, 
                  ai_suggestions: [mockSuggestions],
                  status: 'ai_generated'
                }
              : s
          )
        }));
        
        toast.success('AI suggestions generated!');
      } else {
        // Build context from previous sections
        const context = {};
        for (const section of strategy.sections) {
          if (section.data && Object.keys(section.data).some(k => section.data[k])) {
            context[section.section_id] = section.data;
          }
        }
        
        // Inject organization data into context for better AI generation
        if (organization) {
          context['organization'] = {
            corporate_name: organization.corporate_name,
            industry: organization.industry,
            country: organization.country,
            corporate_brief: organization.corporate_brief,
            subsidiaries: (organization.subsidiaries || []).map(s => s.name).join(', '),
            functions: (organization.functions || []).map(f => f.name).join(', ')
          };
        }
        
        const response = await axios.post(
          `${API_URL}/api/strategy-dev/projects/${strategyId}/generate`,
          { section_id: sectionId, context }
        );
        
        setStrategy(prev => ({
          ...prev,
          sections: prev.sections.map(s => 
            s.section_id === sectionId 
              ? { 
                  ...s, 
                  ai_suggestions: response.data.suggestions,
                  status: 'ai_generated'
                }
              : s
          )
        }));
        
        toast.success('AI suggestions generated!');
      }
    } catch (error) {
      console.error('Error generating AI content:', error);
      toast.error('Failed to generate AI content');
    } finally {
      setGenerating(false);
    }
  };

  // Mock AI suggestions based on section
  const getMockAISuggestion = (sectionId) => {
    const suggestions = {
      company_brief: {
        company_name: 'TechVision Industries',
        industry: 'Enterprise Software & Cloud Solutions',
        size: '1,000 - 2,500 employees',
        founded_year: '2010',
        headquarters: 'Austin, TX',
        description: 'TechVision Industries is a leading provider of enterprise cloud solutions, specializing in digital transformation and AI-powered business intelligence platforms.',
        key_products_services: [
          'Cloud Enterprise Suite',
          'AI Business Intelligence Platform',
          'Digital Transformation Services',
          'Enterprise Integration APIs'
        ]
      },
      vision_mission: {
        vision: 'To empower every organization with intelligent technology solutions that drive innovation, growth, and sustainable success in the digital age.',
        mission: 'We create transformative software solutions that enable businesses to operate smarter, make data-driven decisions, and achieve operational excellence.',
        core_values: [
          { name: 'Innovation First', description: 'Pioneering new solutions that push the boundaries of what\'s possible' },
          { name: 'Customer Partnership', description: 'Building lasting relationships through exceptional service and support' },
          { name: 'Integrity Always', description: 'Maintaining the highest ethical standards in all our dealings' },
          { name: 'Team Excellence', description: 'Fostering a culture of collaboration and continuous improvement' }
        ]
      },
      swot_analysis: {
        strengths: [
          { title: 'Strong Brand Recognition', description: 'Well-established reputation in the enterprise market' },
          { title: 'Innovative Technology', description: 'Cutting-edge AI and cloud capabilities' },
          { title: 'Experienced Team', description: 'Deep industry expertise across all departments' }
        ],
        weaknesses: [
          { title: 'Limited Geographic Reach', description: 'Primary presence in North America only' },
          { title: 'Product Complexity', description: 'Steep learning curve for new users' }
        ],
        opportunities: [
          { title: 'Global Expansion', description: 'Untapped markets in Europe and Asia' },
          { title: 'AI Integration', description: 'Growing demand for AI-powered solutions' }
        ],
        threats: [
          { title: 'Increasing Competition', description: 'New entrants disrupting the market' },
          { title: 'Economic Uncertainty', description: 'Potential impact on enterprise spending' }
        ]
      },
      strategic_objectives: {
        objectives: [
          { id: 'obj-1', title: 'Revenue Growth', description: 'Achieve 25% YoY revenue growth', perspective: 'financial', target_year: 2026 },
          { id: 'obj-2', title: 'Customer Expansion', description: 'Expand customer base by 40%', perspective: 'customer', target_year: 2026 },
          { id: 'obj-3', title: 'Operational Efficiency', description: 'Reduce operational costs by 15%', perspective: 'internal_process', target_year: 2026 },
          { id: 'obj-4', title: 'Talent Development', description: 'Upskill 100% of technical staff', perspective: 'learning_growth', target_year: 2026 }
        ]
      }
    };
    
    return suggestions[sectionId] || { content: 'AI-generated content for this section will appear here.', raw: true };
  };

  // Apply AI suggestion
  const handleApplySuggestion = async (sectionId, suggestion) => {
    const sectionConfig = getSectionConfig(sectionId);
    if (!sectionConfig) return;

    // Transform suggestion to match section fields
    const newData = {};
    for (const field of sectionConfig.fields) {
      if (suggestion[field] !== undefined) {
        newData[field] = suggestion[field];
      }
    }

    await handleSaveSection(sectionId, newData, 'ai_generated');
  };

  // Add comment
  const handleAddComment = async () => {
    if (!newComment.trim() || !activeSection) return;

    try {
      if (USE_MOCK_DATA) {
        const mockComment = {
          id: `comment-${Date.now()}`,
          strategy_id: strategyId,
          section_id: activeSection,
          user_id: 'user-1',
          user_name: 'Test User',
          content: newComment,
          comment_type: 'general',
          resolved: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setComments([...comments, mockComment]);
        setNewComment('');
        toast.success('Comment added');
      } else {
        const response = await axios.post(
          `${API_URL}/api/strategy-dev/projects/${strategyId}/comments`,
          {
            section_id: activeSection,
            content: newComment,
            comment_type: 'general'
          }
        );
        
        setComments([...comments, response.data]);
        setNewComment('');
        toast.success('Comment added');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  // Export strategy
  const handleExport = async (format) => {
    try {
      toast.info(`Generating ${format.toUpperCase()} file...`);

      if (USE_MOCK_DATA) {
        // For mock mode, call the real backend (export works without needing mock data in DB)
        // We'll simulate the export
        await new Promise(resolve => setTimeout(resolve, 1500));
        toast.success(`${format.toUpperCase()} generated! (Demo mode - use API for actual download)`);
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/strategy-dev/projects/${strategyId}/export`,
        { format, include_sections: [], template_style: 'corporate' }
      );

      if (response.data?.download_url) {
        // Trigger download
        const link = document.createElement('a');
        link.href = `${API_URL}${response.data.download_url}`;
        link.download = response.data.filename || `strategy.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`${format.toUpperCase()} downloaded successfully!`);
      }
    } catch (error) {
      console.error('Error exporting strategy:', error);
      toast.error('Failed to export strategy');
    }
  };

  // Link to execution module
  const handleLinkToExecution = async () => {
    if (strategy.linked_to_execution) {
      toast.info('Strategy is already linked to execution module');
      return;
    }

    try {
      toast.info('Linking strategic objectives to execution module...');

      if (USE_MOCK_DATA) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setStrategy(prev => ({
          ...prev,
          linked_to_execution: true,
          execution_link_date: new Date().toISOString()
        }));
        toast.success('Strategy linked to execution module! Objectives and KPIs have been created.');
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/strategy-dev/projects/${strategyId}/link-execution`,
        { objectives_to_link: [], create_kpis: true, create_projects: true }
      );

      setStrategy(prev => ({
        ...prev,
        linked_to_execution: true,
        execution_link_date: new Date().toISOString()
      }));

      toast.success(
        `Linked! ${response.data.objectives_created} objectives and ${response.data.kpis_created} KPIs created in execution module.`
      );
    } catch (error) {
      console.error('Error linking to execution:', error);
      toast.error('Failed to link to execution module');
    }
  };

  // Send projects/KPIs to Project Management module
  const handleSendToProjectMgmt = async () => {
    if (strategy.sent_to_pm) {
      toast.info('Projects already sent to Project Management module');
      return;
    }

    try {
      toast.info('Sending year-1 projects to Project Management...');

      if (USE_MOCK_DATA) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setStrategy(prev => ({
          ...prev,
          sent_to_pm: true,
          pm_link_date: new Date().toISOString()
        }));
        toast.success('Projects sent to Project Management module successfully!');
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/strategy-dev/projects/${strategyId}/send-to-pm`
      );

      setStrategy(prev => ({
        ...prev,
        sent_to_pm: true,
        pm_link_date: new Date().toISOString()
      }));

      toast.success(
        `${response.data.projects_created} projects created in Project Management module!`
      );
    } catch (error) {
      console.error('Error sending to PM:', error);
      const msg = error.response?.data?.detail || 'Failed to send to Project Management';
      toast.error(msg);
    }
  };

  // Fetch version history for current section
  const fetchVersions = async () => {
    if (!activeSection) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/strategy-dev/projects/${strategyId}/versions?section_id=${activeSection}`
      );
      setVersions(res.data);
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  // Restore a version
  const handleRestoreVersion = async (versionId) => {
    try {
      toast.info('Restoring version...');
      await axios.post(
        `${API_URL}/api/strategy-dev/projects/${strategyId}/versions/${versionId}/restore`
      );
      // Re-fetch strategy to reflect restored data
      const res = await axios.get(`${API_URL}/api/strategy-dev/projects/${strategyId}`);
      setStrategy(res.data);
      await fetchVersions();
      toast.success('Version restored');
    } catch (err) {
      console.error('Error restoring version:', err);
      toast.error('Failed to restore version');
    }
  };

  // Search across strategies
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await axios.get(`${API_URL}/api/strategy-dev/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch (err) {
      console.error('Error searching:', err);
      toast.error('Search failed');
    }
  };

  // Render section content editor
  const renderSectionEditor = () => {
    const section = getCurrentSection();
    const config = getSectionConfig(activeSection);
    
    if (!section || !config) return null;

    const Icon = sectionIcons[config.icon] || Target;

    return (
      <div className="space-y-6">
        {/* Section Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-primary/10">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{config.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">{config.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={statusColors[section.status]}>
              {statusLabels[section.status]}
            </Badge>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleGenerateAI(activeSection)}
              disabled={generating}
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {generating ? 'Generating...' : 'AI Generate'}
            </Button>
          </div>
        </div>

        {/* AI Suggestions Panel */}
        {section.ai_suggestions && section.ai_suggestions.length > 0 && (
          <Card className="bg-purple-500/5 border-purple-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm text-purple-400">
                <Sparkles className="w-4 h-4" />
                AI Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {section.ai_suggestions.map((suggestion, idx) => (
                  <div key={idx} className="p-3 bg-card rounded-lg border border-border">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 text-sm text-foreground">
                        {suggestion.raw ? (
                          <pre className="whitespace-pre-wrap font-sans">{suggestion.content}</pre>
                        ) : (
                          <div className="space-y-2">
                            {Object.entries(suggestion).slice(0, 3).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium capitalize">{key.replace(/_/g, ' ')}: </span>
                                <span className="text-muted-foreground">
                                  {typeof value === 'string' 
                                    ? value.substring(0, 100) + (value.length > 100 ? '...' : '')
                                    : Array.isArray(value)
                                      ? `${value.length} items`
                                      : JSON.stringify(value).substring(0, 50)
                                  }
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => handleApplySuggestion(activeSection, suggestion)}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Apply
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section Fields */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {config.fields.map(field => (
                <div key={field} className="space-y-3">
                  <Label className="capitalize text-sm font-medium">
                    {strategy?.entity_type === 'function' && functionFieldOverrides[field]
                      ? functionFieldOverrides[field]
                      : field.replace(/_/g, ' ')}
                  </Label>
                  {renderFieldInput(field, section.data?.[field], (value) => {
                    const newData = { ...section.data, [field]: value };
                    // Update local state immediately
                    setStrategy(prev => ({
                      ...prev,
                      sections: prev.sections.map(s => 
                        s.section_id === activeSection 
                          ? { ...s, data: newData }
                          : s
                      )
                    }));
                  })}
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 mt-8 pt-5 border-t border-border">
              <Button 
                variant={section.status === 'in_progress' ? 'default' : 'outline'}
                onClick={() => handleSaveSection(activeSection, section.data, section.status === 'in_progress' ? 'not_started' : 'in_progress')}
                disabled={saving}
                data-testid="save-draft-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Draft
              </Button>
              <Button 
                variant={section.status === 'reviewed' ? 'default' : 'outline'}
                onClick={() => handleSaveSection(activeSection, section.data, section.status === 'reviewed' ? 'in_progress' : 'reviewed')}
                disabled={saving}
                data-testid="mark-reviewed-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Reviewed
              </Button>
              <Button 
                variant={section.status === 'approved' ? 'default' : 'outline'}
                className={section.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
                onClick={() => handleSaveSection(activeSection, section.data, section.status === 'approved' ? 'reviewed' : 'approved')}
                disabled={saving}
                data-testid="mark-approved-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Approved
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render appropriate input for field type
  const renderFieldInput = (field, value, onChange) => {
    const getFieldLabel = (f) => {
      if (strategy?.entity_type === 'function' && functionFieldOverrides[f]) {
        return functionFieldOverrides[f];
      }
      return f.replace(/_/g, ' ');
    };
    // Array fields
    if (['key_achievements', 'sector_presence', 'core_values', 'strengths', 'weaknesses', 
         'opportunities', 'threats', 'objectives', 'challenges'].includes(field)) {
      return (
        <div className="space-y-2">
          {Array.isArray(value) && value.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                value={typeof item === 'string' ? item : item.title || item.name || JSON.stringify(item)}
                onChange={(e) => {
                  const newArray = [...value];
                  newArray[idx] = e.target.value;
                  onChange(newArray);
                }}
                className="flex-1"
              />
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => onChange(value.filter((_, i) => i !== idx))}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onChange([...(value || []), ''])}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>
      );
    }

    // Long text fields
    if (['description', 'vision', 'mission', 'mandate', 'digital_vision'].includes(field)) {
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={`Enter ${getFieldLabel(field)}...`}
        />
      );
    }

    // Object fields - render as JSON for now
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return (
        <Textarea
          value={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              // Keep as string if not valid JSON
            }
          }}
          rows={6}
          className="font-mono text-sm"
          placeholder={`Enter ${getFieldLabel(field)} as JSON...`}
        />
      );
    }

    // Default: text input
    return (
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${getFieldLabel(field)}...`}
      />
    );
  };

  // Section comments for current section
  const sectionComments = comments.filter(c => c.section_id === activeSection);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!strategy) return null;

  return (
    <div className="h-screen bg-background flex">
      {/* Sidebar - Sections Navigation */}
      <div className="w-72 border-r border-border bg-card/50 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-3"
            onClick={() => navigate('/strategy-development')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h2 className="font-semibold text-foreground truncate">{strategy.name}</h2>
          <p className="text-sm text-muted-foreground">
            {strategy.year} {strategy.entity_name || 'Corporate'} Strategy
          </p>
          
          {/* Progress */}
          <div className="mt-3 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{calculateProgress()}%</span>
            </div>
            <Progress value={calculateProgress()} className="h-2" />
          </div>
        </div>

        {/* Sections List */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {sectionsConfig.map(rawConfig => {
              const config = getSectionConfig(rawConfig.id) || rawConfig;
              const section = strategy.sections?.find(s => s.section_id === rawConfig.id);
              const Icon = sectionIcons[rawConfig.icon] || Target;
              const isActive = activeSection === rawConfig.id;
              
              return (
                <button
                  key={rawConfig.id}
                  onClick={() => setActiveSection(rawConfig.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                    isActive 
                      ? 'bg-primary/10 text-primary' 
                      : 'hover:bg-muted text-foreground'
                  }`}
                  data-testid={`section-nav-${rawConfig.id}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{config.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${statusColors[section?.status || 'not_started']}`}>
                        {statusLabels[section?.status || 'not_started']}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${isActive ? 'rotate-90' : ''}`} />
                </button>
              );
            })}
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="p-4 border-t border-border space-y-2">
          <Button variant="outline" className="w-full justify-start" onClick={() => setShowComments(!showComments)}>
            <MessageSquare className="w-4 h-4 mr-2" />
            Comments ({comments.length})
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchVersions(); }} data-testid="show-history-btn">
            <Clock className="w-4 h-4 mr-2" />
            Version History
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => setShowSearch(!showSearch)} data-testid="show-search-btn">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-start" data-testid="export-strategy-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => handleExport('pptx')} data-testid="export-pptx-btn">
                PowerPoint (.pptx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('docx')} data-testid="export-docx-btn">
                Word Document (.docx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            className="w-full justify-start"
            disabled={calculateProgress() < 30}
            onClick={handleLinkToExecution}
            data-testid="link-to-execution-btn"
          >
            <Link2 className="w-4 h-4 mr-2" />
            {strategy.linked_to_execution ? 'Linked to Execution' : 'Send to Execution'}
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            disabled={!strategy.linked_to_execution || strategy.sent_to_pm}
            onClick={handleSendToProjectMgmt}
            data-testid="send-to-pm-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {strategy.sent_to_pm ? 'Sent to Projects' : 'Send to Projects'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        <div className={`flex-1 p-8 overflow-auto ${(showComments || showHistory || showSearch) ? 'mr-80' : ''}`}>
          {renderSectionEditor()}
        </div>

        {/* Comments Panel */}
        {showComments && (
          <div className="w-80 border-l border-border bg-card/50 flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Comments</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowComments(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <ScrollArea className="flex-1 p-4">
              {sectionComments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No comments on this section yet
                </p>
              ) : (
                <div className="space-y-4">
                  {sectionComments.map(comment => (
                    <div key={comment.id} className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                          {comment.user_name?.charAt(0) || 'U'}
                        </div>
                        <span className="text-sm font-medium text-foreground">{comment.user_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-foreground">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Add Comment */}
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <Button size="icon" onClick={handleAddComment} disabled={!newComment.trim()}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Version History Panel */}
        {showHistory && (
          <div className="w-80 border-l border-border bg-card/50 flex flex-col" data-testid="version-history-panel">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Version History</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowHistory(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <ScrollArea className="flex-1 p-4">
              {versions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No versions recorded yet. Save a section to create a version.
                </p>
              ) : (
                <div className="space-y-3">
                  {versions.map(v => (
                    <div key={v.id} className="p-3 bg-muted/50 rounded-lg border border-border/50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">Version {v.version_number}</span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setVersionModalData(v)}
                            data-testid={`view-version-${v.id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => handleRestoreVersion(v.id)}
                            data-testid={`restore-version-${v.id}`}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Restore
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{v.change_summary}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {v.changed_by_name} · {new Date(v.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {/* Search Panel */}
        {showSearch && (
          <div className="w-80 border-l border-border bg-card/50 flex flex-col" data-testid="search-panel">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Search</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowSearch(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-4 border-b border-border">
              <div className="flex gap-2">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search across strategies..."
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="search-input"
                />
                <Button size="icon" onClick={handleSearch} disabled={!searchQuery.trim()}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <ScrollArea className="flex-1 p-4">
              {searchResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {searchQuery ? 'No results found' : 'Enter a search term'}
                </p>
              ) : (
                <div className="space-y-3">
                  {searchResults.map(result => (
                    <div
                      key={result.strategy_id}
                      className="p-3 bg-muted/50 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => {
                        if (result.strategy_id !== strategyId) {
                          navigate(`/strategy-development/${result.strategy_id}`);
                        }
                      }}
                    >
                      <p className="text-sm font-medium text-foreground">{result.strategy_name}</p>
                      {result.entity_name && (
                        <p className="text-xs text-muted-foreground">{result.entity_name}</p>
                      )}
                      <div className="mt-2 space-y-1">
                        {result.matches.slice(0, 3).map((match, i) => (
                          <p key={i} className="text-xs text-muted-foreground truncate">
                            <span className="text-primary/70">{match.field}:</span> {match.value}
                          </p>
                        ))}
                        {result.matches.length > 3 && (
                          <p className="text-xs text-muted-foreground">+{result.matches.length - 3} more matches</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Version View Modal */}
      <Dialog open={!!versionModalData} onOpenChange={() => setVersionModalData(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              Version {versionModalData?.version_number}
              <span className="text-sm font-normal text-muted-foreground ml-2">
                {versionModalData && new Date(versionModalData.created_at).toLocaleString()}
              </span>
            </DialogTitle>
          </DialogHeader>
          {versionModalData?.data && (
            <div className="space-y-3 py-4">
              {Object.entries(versionModalData.data).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs text-muted-foreground capitalize">
                    {key.replace(/_/g, ' ')}
                  </Label>
                  {Array.isArray(value) ? (
                    <div className="space-y-1">
                      {value.map((item, i) => (
                        <p key={i} className="text-sm text-foreground bg-muted/50 p-2 rounded">
                          {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                        </p>
                      ))}
                      {value.length === 0 && <p className="text-sm text-muted-foreground italic">Empty</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-foreground bg-muted/50 p-2 rounded whitespace-pre-wrap">
                      {value || <span className="text-muted-foreground italic">Empty</span>}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" onClick={() => setVersionModalData(null)}>
              Close
            </Button>
            <Button onClick={() => { handleRestoreVersion(versionModalData.id); setVersionModalData(null); }}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Restore This Version
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrategyEditor;
